import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { AppPicker } from "@/modules/shell/AppPicker";
import { listWarehouses } from "@/shared/api/inventory";

export type WarehouseOption = {
  id: string;
  code: string;
  name: string;
  isDefault: boolean;
};

type Props = {
  companySlug: string;
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
  className?: string;
  /** Accessible label when the select is shown */
  label?: string;
  /** When true (and N>1), keep null as “All warehouses” instead of auto-picking default */
  allowAll?: boolean;
};

/**
 * Warehouse picker — hidden when the org has only one warehouse (plan/09).
 * When N>1, searchable picker; defaults to org default unless `allowAll`.
 */
export function WarehouseSelect({
  companySlug,
  value,
  onChange,
  disabled,
  className,
  label = "Warehouse",
  allowAll = false,
}: Props) {
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listWarehouses(companySlug)
      .then((d) => {
        if (cancelled) return;
        const rows = d.warehouses;
        setWarehouses(rows);
        if (rows.length <= 1) {
          onChange(null);
          return;
        }
        if (!allowAll && !value) {
          const def = rows.find((w) => w.isDefault) ?? rows[0] ?? null;
          if (def) onChange(def.id);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed");
        }
      });
    return () => {
      cancelled = true;
    };
    // Intentionally once per company — avoid re-picking when parent value changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug]);

  const options = useMemo(
    () =>
      warehouses.map((w) => ({
        value: w.id,
        label: w.name,
        description: `${w.code}${w.isDefault ? " · default" : ""}`,
        keywords: `${w.code} ${w.name}`,
      })),
    [warehouses],
  );

  if (error) {
    return <Alert variant="destructive" className="py-2 text-xs">{error}</Alert>;
  }
  if (warehouses.length <= 1) return null;

  return (
    <div>
      <Label>{label}</Label>
      <AppPicker
        className={className ?? "mt-1 max-w-xs"}
        title={label}
        description="Search warehouses"
        placeholder="Choose warehouse"
        value={value}
        options={options}
        disabled={disabled || warehouses.length === 0}
        clearLabel={allowAll ? "All warehouses" : undefined}
        emptyLabel="No warehouses"
        onChange={onChange}
      />
    </div>
  );
}
